import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  SALES_INSIGHTS_OTHERS_KEY,
  SalesInsightsBreakdownResponse,
  SalesInsightsBreakdownRow
} from '../../models/sales-insights.model';
import { formatSalesInsightsAmount, formatSalesInsightsPercent } from '../../utils/sales-insights-format';
import { salesInsightsFilterLabel, salesInsightsNoValueLabel } from '../../utils/sales-insights-labels';
import {
  SalesInsightsProgressBar,
  salesInsightsProgressBar,
  salesInsightsProgressScale
} from '../../utils/sales-insights-targets';
import { SALES_INSIGHTS_SEARCH_MIN_ROWS, salesInsightsMatches } from '../../utils/sales-insights-search';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

type ProgressMeasure = 'valueBase' | 'valueLocal' | 'quantity' | 'rowCount';

const MEASURES: ProgressMeasure[] = ['valueBase', 'valueLocal', 'quantity', 'rowCount'];

export interface SalesInsightsProgressRow {
  row: SalesInsightsBreakdownRow;
  name: string;
  code: string | null;
  value: number | null;
  target: number | null;
  bar: SalesInsightsProgressBar;
  percentText: string;
}

@Component({
  selector: 'lib-sales-insights-progress-table',
  standalone: false,
  templateUrl: './sales-insights-progress-table.component.html',
  styleUrl: './sales-insights-progress-table.component.scss'
})
export class SalesInsightsProgressTableComponent extends SalesInsightsBaseComponent<SalesInsightsBreakdownResponse> {
  protected translate = inject(TranslateService);

  readonly formatAmount = formatSalesInsightsAmount;

  rows: SalesInsightsProgressRow[] = [];
  query = '';
  private allRows: SalesInsightsProgressRow[] = [];
  exporting = false;

  get measure(): ProgressMeasure {
    return MEASURES.includes(this.options.measure) ? this.options.measure : 'valueBase';
  }

  get groupBy(): string | undefined {
    return this.config?.source?.params?.groupBy as string | undefined;
  }

  get nameHeader(): string {
    return this.groupBy
      ? salesInsightsFilterLabel(this.translate, this.groupBy)
      : this.translate.instant('SALES_INSIGHTS_COL_NAME');
  }

  get valueHeaderKey(): string {
    return `SALES_INSIGHTS_COL_${this.measure.replace(/([A-Z])/g, '_$1').toUpperCase()}`;
  }

  get barColor(): string {
    return `rgb(${this.colorRgb})`;
  }

  get canExport(): boolean {
    return !!this.options.export && !this.loading && !this.error && this.rows.length > 0;
  }

  get searchable(): boolean {
    return this.options.search !== false && !this.loading && this.allRows.length >= SALES_INSIGHTS_SEARCH_MIN_ROWS;
  }

  search(query: string): void {
    this.query = query;
    this.rows = this.matching();
  }

  protected defaultSubtitle(): string {
    return this.periodLabelKey();
  }

  protected isEmpty(data: SalesInsightsBreakdownResponse | null): boolean {
    return !data?.rows?.length;
  }

  protected onData(data: SalesInsightsBreakdownResponse | null): void {
    const source = data?.rows || [];
    const regular = source
      .filter(row => row.key !== SALES_INSIGHTS_OTHERS_KEY)
      .sort((a, b) => (b[this.measure] ?? 0) - (a[this.measure] ?? 0));
    const ordered = [...regular, ...source.filter(row => row.key === SALES_INSIGHTS_OTHERS_KEY)];
    const inputs = ordered.map(row => ({ value: row[this.measure], target: row.target ?? null }));
    const scale = salesInsightsProgressScale(inputs);
    this.allRows = ordered.map((row, i) => {
      const bar = salesInsightsProgressBar(inputs[i], scale);
      return {
        row,
        name: this.displayName(row),
        code: !!row.key && !!row.name && row.name !== row.key && row.key !== SALES_INSIGHTS_OTHERS_KEY ? row.key : null,
        value: row[this.measure],
        target: row.target ?? null,
        bar,
        percentText: bar.attainment !== null ? formatSalesInsightsPercent(bar.attainment) : ''
      };
    });
    this.rows = this.matching();
  }

  private matching(): SalesInsightsProgressRow[] {
    return this.allRows.filter(item => salesInsightsMatches(this.query, [item.name, item.code, item.row.key]));
  }

  async exportExcel(): Promise<void> {
    this.exporting = true;
    try {
      const XLSX = await import('xlsx');
      const t = (key: string) => this.translate.instant(key);
      const header = [this.nameHeader, t(this.valueHeaderKey), t('SALES_INSIGHTS_COL_TARGET'), t('SALES_INSIGHTS_COL_ATTAINMENT')];
      const body = this.rows.map(row => [
        row.name,
        row.value ?? null,
        row.target,
        row.bar.attainment !== null ? Math.round(row.bar.attainment * 10) / 10 : null
      ]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...body]), 'Data');
      XLSX.writeFile(workbook, `${this.fileName()}.xlsx`);
    } finally {
      this.exporting = false;
    }
  }

  private displayName(row: SalesInsightsBreakdownRow): string {
    if (row.key === SALES_INSIGHTS_OTHERS_KEY) {
      return this.translate.instant('SALES_INSIGHTS_OTHERS');
    }
    if (!row.key) {
      return salesInsightsNoValueLabel(this.translate, this.groupBy);
    }
    return row.name || row.key;
  }

  private fileName(): string {
    const title = this.translate.instant(this.config?.title || 'SALES_INSIGHTS');
    return title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'sales-insights';
  }
}
